import type { PriceUnitDto } from '@/api';
import {
  applyNumericFilter,
  compareNumericValues,
} from '@/components/table/table-helpers';
import type { NumericFilterValue } from '@/components/table/table-types';
import FilterDropdown from '@/components/table/toolbar/filter-dropdown';
import NumericColumnFilter from '@/components/table/toolbar/numeric-column-filter';
import { connectColors, themeColors } from '@/theme/colors';
import { Flex, Text } from '@lego/klik-ui';
import { Tooltip as MuiTooltip } from '@mui/material';
import type { Row } from '@tanstack/react-table';
import type { MRT_Column, MRT_ColumnDef } from 'material-react-table';
import type { HarmonizationPivotRowForDisplay } from '../../utils/simulation-types';

const DEFAULT_PRICE_LIST_COLOR = {
  header: connectColors.core.slate[200],
  sub: connectColors.core.slate[50],
};

const PRICE_LIST_COLORS = [
  DEFAULT_PRICE_LIST_COLOR,
  { header: connectColors.core.slate[100], sub: connectColors.core.slate[25] },
];

const ALT_PRICELIST_COLORS = {
  header: connectColors.core.blue[200],
  sub: connectColors.core.blue[50],
};

const INDEX_COLORS = {
  aboveThreshold: connectColors.core.teal[50],
  belowThreshold: connectColors.core.orange[50],
};

const PRODUCT_GROUP_HEADER_BG = connectColors.core.azure[200];
const PINNED_HEADER_BG = connectColors.core.azure[50];
const INDEX_THRESHOLD_HIGH = 110;
const INDEX_THRESHOLD_LOW = 90;

export const EXPAND_COLUMN_WIDTH = 42;
const THEME_COL_WIDTH = 150;
const PRODUCT_ID_COL_WIDTH = 100;
const PRODUCT_NAME_COL_WIDTH = 200;
const LAUNCH_STATUS_COL_WIDTH = 120;

const PINNED_SECTION_WIDTH =
  EXPAND_COLUMN_WIDTH +
  THEME_COL_WIDTH +
  PRODUCT_ID_COL_WIDTH +
  PRODUCT_NAME_COL_WIDTH +
  LAUNCH_STATUS_COL_WIDTH;

const PINNED_COLUMNS = [
  'mrt-row-expand',
  'product-info-group',
  'themeName',
  'productId',
  'productName',
  'launchStatus',
];

const GROUPING = ['themeName'];

const DEFAULT_SORTING = [
  { id: 'themeName', desc: false },
  { id: 'productId', desc: false },
];

const meanExcludingZero = (
  columnId: string,
  leafRows: Row<HarmonizationPivotRowForDisplay>[],
): number | undefined => {
  let count = 0;
  let sum = 0;
  for (const row of leafRows) {
    const value = row.getValue<number | null>(columnId);
    if (value != null && value !== 0) {
      sum += value;
      count++;
    }
  }
  return count > 0 ? sum / count : 0;
};

const getIndexCellBackgroundColor = (
  value: number | null | undefined,
): string | undefined => {
  if (value == null) return undefined;
  if (value >= INDEX_THRESHOLD_HIGH) return INDEX_COLORS.aboveThreshold;
  if (value <= INDEX_THRESHOLD_LOW) return INDEX_COLORS.belowThreshold;
  return undefined;
};

const pinnedBeforeOverride = (bg: string) => ({
  '&[data-pinned="true"]::before': {
    backgroundColor: `${bg} !important`,
  },
});

const renderHeaderWithTooltip = (label: string) => () => (
  <MuiTooltip title={label} placement="top">
    <Flex alignItems="center" gap="4px" minHeight="28px">
      <Text className="body-xs-medium">{label}</Text>
    </Flex>
  </MuiTooltip>
);

const getFilterOptions = (
  column: MRT_Column<HarmonizationPivotRowForDisplay>,
) =>
  Array.from(
    new Set(
      column
        .getFacetedRowModel()
        .flatRows.map((row) => String(row.getValue(column.id) ?? '')),
    ),
  )
    .sort(compareNumericValues)
    .map((value) => ({
      id: value,
      label: value === '' ? '(empty)' : value,
    }));

const numericColumnFilter: Pick<
  MRT_ColumnDef<HarmonizationPivotRowForDisplay>,
  'filterFn' | 'Filter' | 'sortingFn'
> = {
  sortingFn: (rowA, rowB, columnId) =>
    compareNumericValues(rowA.getValue(columnId), rowB.getValue(columnId)),
  filterFn: (row, columnId, filterValue: NumericFilterValue | undefined) =>
    applyNumericFilter(
      row.getValue<number | string | null>(columnId) ?? '',
      filterValue,
    ),
  Filter: ({ column }) => (
    <NumericColumnFilter
      valueOptions={getFilterOptions(column)}
      value={column.getFilterValue() as NumericFilterValue | undefined}
      onChange={(value) => column.setFilterValue(value)}
    />
  ),
};

const createPinnedColumn = (
  key: string,
  header: string,
  size: number,
  enableGrouping = false,
): MRT_ColumnDef<HarmonizationPivotRowForDisplay> => ({
  accessorKey: key,
  header,
  Header: renderHeaderWithTooltip(header),
  size,
  grow: 0,
  enableGrouping,
  filterFn: (row, columnId, filterValue: string[]) =>
    filterValue.includes(String(row.getValue(columnId) ?? '')),
  Filter: ({ column }) => (
    <FilterDropdown
      options={getFilterOptions(column)}
      value={column.getFilterValue() as string[] | undefined}
      onChange={(selected) =>
        column.setFilterValue(selected.length > 0 ? selected : undefined)
      }
    />
  ),
  muiTableHeadCellProps: {
    align: 'left',
    sx: {
      backgroundColor: PINNED_HEADER_BG,
      ...pinnedBeforeOverride(PINNED_HEADER_BG),
    },
  },
  muiTableBodyCellProps: {
    align: 'left',
    sx: {
      backgroundColor: 'white',
      ...pinnedBeforeOverride('white'),
    },
  },
});

const getPinnedColumns =
  (): MRT_ColumnDef<HarmonizationPivotRowForDisplay>[] => [
    createPinnedColumn('themeName', 'Theme', THEME_COL_WIDTH, true),
    createPinnedColumn('productId', 'Product ID', PRODUCT_ID_COL_WIDTH),
    createPinnedColumn(
      'productName',
      'Product Description',
      PRODUCT_NAME_COL_WIDTH,
    ),
    createPinnedColumn(
      'launchStatus',
      'Product Status',
      LAUNCH_STATUS_COL_WIDTH,
    ),
  ];

const AggregatedIndexCell = ({
  cell,
}: {
  cell: { getValue: () => unknown };
}) => {
  const raw = cell.getValue();
  const value = typeof raw === 'number' ? raw : null;
  return (
    <Text fontWeight="bold">
      {value != null ? Math.round(value).toString() : '—'}
    </Text>
  );
};

type PriceColumnColors = {
  sub: string;
};

const createStaticPriceColumn = (
  plId: number,
  field: 'localRrp' | 'localLp' | 'localNip',
  header: string,
  colors: PriceColumnColors,
  bodyCellBg: string,
): MRT_ColumnDef<HarmonizationPivotRowForDisplay> => ({
  id: `pl_${plId}_${field}`,
  header,
  Header: renderHeaderWithTooltip(header),
  size: 110,
  enableGrouping: false,
  ...numericColumnFilter,
  accessorFn: (row) => row.priceListData[plId]?.[field] ?? null,
  Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
  muiTableHeadCellProps: {
    align: 'right',
    sx: {
      backgroundColor: colors.sub,
      ...pinnedBeforeOverride(colors.sub),
    },
  },
  muiTableBodyCellProps: {
    align: 'right',
    sx: {
      backgroundColor: bodyCellBg,
      ...pinnedBeforeOverride(bodyCellBg),
    },
  },
});

const createIndexColumn = (
  plId: number,
  field: 'altRrpIndex' | 'altLpIndex' | 'altNipIndex',
  header: string,
  colors: PriceColumnColors,
  bodyCellBg: string,
): MRT_ColumnDef<HarmonizationPivotRowForDisplay> => ({
  id: `pl_${plId}_${field}`,
  header,
  Header: renderHeaderWithTooltip(header),
  size: 130,
  enableGrouping: false,
  ...numericColumnFilter,
  accessorFn: (row) => row.priceListData[plId]?.[field] ?? null,
  aggregationFn: meanExcludingZero,
  Cell: ({ cell }) => cell.getValue<number | null>() ?? '-',
  AggregatedCell: AggregatedIndexCell,
  muiTableHeadCellProps: {
    align: 'right',
    sx: {
      backgroundColor: colors.sub,
      ...pinnedBeforeOverride(colors.sub),
    },
  },
  muiTableBodyCellProps: ({ cell }) => {
    const value = cell.getValue<number | null>();
    const bg = getIndexCellBackgroundColor(value) ?? bodyCellBg;
    return {
      align: 'right',
      sx: {
        backgroundColor: bg,
        ...pinnedBeforeOverride(bg),
      },
    };
  },
});

const createPriceListColumnGroup = (
  priceList: PriceUnitDto,
  colorIndex: number,
  alternativePriceListName: string | null,
  isAlternativePriceList: boolean,
): MRT_ColumnDef<HarmonizationPivotRowForDisplay> => {
  const colors = isAlternativePriceList
    ? ALT_PRICELIST_COLORS
    : (PRICE_LIST_COLORS[colorIndex % PRICE_LIST_COLORS.length] ??
      DEFAULT_PRICE_LIST_COLOR);
  const plId = priceList.id;
  const altSuffix =
    !isAlternativePriceList && alternativePriceListName
      ? ` (vs ${alternativePriceListName})`
      : '';

  const bodyCellBg = isAlternativePriceList
    ? themeColors.interactive.primary.subtle.selected
    : connectColors.core.slate[10];

  return {
    id: `price-list-${plId}`,
    header: `${plId} - ${priceList.name || `Price List ${plId}`}${altSuffix}`,
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: colors.header,
        fontWeight: 'bold',
        ...(isAlternativePriceList && {
          position: 'sticky',
          left: PINNED_SECTION_WIDTH,
          zIndex: 3,
        }),
      },
    },
    columns: [
      createStaticPriceColumn(
        plId,
        'localRrp',
        'Local RRP',
        colors,
        bodyCellBg,
      ),
      createIndexColumn(
        plId,
        'altRrpIndex',
        'Alt RRP Index',
        colors,
        bodyCellBg,
      ),
      createStaticPriceColumn(plId, 'localLp', 'Local LP', colors, bodyCellBg),
      createIndexColumn(plId, 'altLpIndex', 'Alt LP Index', colors, bodyCellBg),
      createStaticPriceColumn(
        plId,
        'localNip',
        'Local NIP',
        colors,
        bodyCellBg,
      ),
      createIndexColumn(
        plId,
        'altNipIndex',
        'Alt NIP Index',
        colors,
        bodyCellBg,
      ),
    ],
  };
};

export const getHarmonizationColumnPinning = (
  alternativePriceListId: number,
) => ({
  left: [
    ...PINNED_COLUMNS,
    `pl_${alternativePriceListId}_localRrp`,
    `pl_${alternativePriceListId}_altRrpIndex`,
    `pl_${alternativePriceListId}_localLp`,
    `pl_${alternativePriceListId}_altLpIndex`,
    `pl_${alternativePriceListId}_localNip`,
    `pl_${alternativePriceListId}_altNipIndex`,
  ],
});

export const getHarmonizationGrouping = (): string[] => [...GROUPING];

export const getHarmonizationSorting = () =>
  DEFAULT_SORTING.map((s) => ({ ...s }));

export const getHarmonizationColumns = (
  priceLists: PriceUnitDto[],
  alternativePriceListId: number,
): MRT_ColumnDef<HarmonizationPivotRowForDisplay>[] => {
  const altPriceList = priceLists.find(
    (pl) => pl.id === alternativePriceListId,
  );
  const otherPriceLists = priceLists.filter(
    (pl) => pl.id !== alternativePriceListId,
  );
  const alternativePriceListName = altPriceList?.name ?? null;

  return [
    {
      id: 'product-info-group',
      header: 'Product Data',
      muiTableHeadCellProps: {
        sx: {
          backgroundColor: PRODUCT_GROUP_HEADER_BG,
          fontWeight: 'bold',
          position: 'sticky',
          left: EXPAND_COLUMN_WIDTH,
          zIndex: 3,
        },
      },
      columns: getPinnedColumns(),
    },
    ...(altPriceList
      ? [
          createPriceListColumnGroup(
            altPriceList,
            0,
            alternativePriceListName,
            true,
          ),
        ]
      : []),
    ...otherPriceLists.map((priceList, index) =>
      createPriceListColumnGroup(
        priceList,
        index,
        alternativePriceListName,
        false,
      ),
    ),
  ];
};
